import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { SyntheticEventData, Simulate } from 'react-dom/test-utils';

import { baseUniDriverFactory, ReactBase } from '../utils/test-utils/unidriver';
import { statusIndicatorDriverFactory } from '../StatusIndicator/StatusIndicator.uni.driver';
import { StatusIndications } from '../common';

import { dataAttr, dataHooks } from './constants';

export interface InputAreaUniDriver extends BaseUniDriver {
  trigger: (
    trigger: keyof typeof Simulate,
    event: SyntheticEventData,
  ) => Promise<void>;
  focus: () => Promise<void>;
  enterText: (text: string) => Promise<void>;
  getValue: () => Promise<string>;
  getName: () => Promise<string | null>;
  getPlaceholder: () => Promise<any>;
  getDefaultValue: () => Promise<any>;
  getRowsCount: () => Promise<any>;
  getMaxLength: () => Promise<any>;
  getTabIndex: () => Promise<any>;
  getReadOnly: () => Promise<any>;
  getResizable: () => Promise<boolean>;
  getDisabled: () => Promise<boolean>;
  getRequired: () => Promise<boolean>;
  getHasCounter: () => Promise<boolean>;
  getCounterValue: () => Promise<string>;
  hasExclamation: () => Promise<boolean>;
  isFocusedStyle: () => Promise<boolean>;
  isSizeSmall: () => Promise<boolean>;
  isHoveredStyle: () => Promise<boolean>;
  isFocus: () => Promise<boolean>;
  getStyle: () => Promise<any>;
  getAriaLabel: () => Promise<string | null>;
  getAriaControls: () => Promise<string | null>;
  getAriaDescribedby: () => Promise<string | null>;
  getTooltipDataHook: () => string;
  getTooltipElement: () => UniDriver;
  getCursorLocation: () => Promise<number>;

  // Status
  hasStatus: (status: StatusIndications) => Promise<boolean>;
  getStatusMessage: () => Promise<string | null>;
}

export const inputAreaUniDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): InputAreaUniDriver => {
  const textArea = base.$('textarea');
  const counterSelector = '[data-hook="counter"]';
  const getStatusIndicatorDriver = () =>
    statusIndicatorDriverFactory(
      base.$(`[data-hook="${dataHooks.tooltip}"]`),
      body,
    );

  const textAreaBase = ReactBase(textArea);

  return {
    ...baseUniDriverFactory(base),

    trigger: (trigger, event) => (textAreaBase as any)[trigger](event),
    focus: () => textAreaBase.focus(),
    enterText: (text: string) => textArea.enterValue(text),
    getValue: () => textArea.value(),
    getName: () => textArea.attr('name'),
    getPlaceholder: () => textArea._prop('placeholder'),
    getDefaultValue: () => textArea._prop('defaultValue'),
    getRowsCount: () => textArea._prop('rows'),
    getMaxLength: () => textArea._prop('maxLength'),
    getTabIndex: () => textArea._prop('tabIndex'),
    getReadOnly: () => textArea._prop('readOnly'),
    getCursorLocation: () => textArea._prop('selectionStart'),
    getResizable: async () => !!(await base.attr(dataAttr.RESIZABLE)),
    getDisabled: async () =>
      !!(await base.attr(dataAttr.DISABLED)) &&
      !!(await textArea._prop('disabled')),
    getRequired: () => textArea._prop('required'),
    getHasCounter: async () => (await base.$$(counterSelector).count()) > 0,
    getCounterValue: () => base.$(counterSelector).text(),
    hasExclamation: () => getStatusIndicatorDriver().exists(),
    isFocusedStyle: async () => !!(await base.attr(dataAttr.FOCUS)),
    isSizeSmall: async () => (await base.attr(dataAttr.SIZE)) === 'small',
    isHoveredStyle: async () => !!(await base.attr(dataAttr.HOVER)),
    isFocus: () => textAreaBase.isFocus(),
    exists: () => textArea.exists(),
    getStyle: () => textArea._prop('style'),
    getAriaLabel: () => textArea.attr('aria-label'),
    getAriaControls: () => textArea.attr('aria-controls'),
    getAriaDescribedby: () => textArea.attr('aria-describedby'),
    getTooltipDataHook: () => dataHooks.tooltip,
    getTooltipElement: () => base,

    // Status
    /** Return true if there's a status */
    hasStatus: async (status: StatusIndications) => {
      const statusIndicatorDriver = getStatusIndicatorDriver();

      if (await statusIndicatorDriver.exists()) {
        return (
          (await base.attr(dataAttr.STATUS)) === status &&
          (await statusIndicatorDriver.getStatus()) === status
        );
      }

      return (await base.attr(dataAttr.STATUS)) === status;
    },
    /** If there's a status message, returns its text value */
    getStatusMessage: async () => {
      const statusIndicatorDriver = getStatusIndicatorDriver();
      let tooltipText: string | null = null;

      if (await statusIndicatorDriver.hasMessage()) {
        tooltipText = await statusIndicatorDriver.getMessage();
      }

      return tooltipText;
    },
  };
};
